import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({
  isDarkMode: false,
  setIsDarkMode: () => {},
  toggleDarkMode: () => {},
});

export function ThemeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      return stored === 'true';
    }
    // Default to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    // Apply dark class to html element
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Persist preference
    localStorage.setItem('darkMode', isDarkMode.toString());
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode(prev => !prev);

  return (
    <ThemeContext.Provider value={{ isDarkMode, setIsDarkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Hook to force dark mode on specific pages
export function useForceDarkMode(forceDark = true) {
  const { setIsDarkMode } = useTheme();
  
  useEffect(() => {
    const previousState = document.documentElement.classList.contains('dark');
    
    if (forceDark) {
      document.documentElement.classList.add('dark');
    }
    
    // Cleanup: restore previous state when component unmounts
    return () => {
      if (forceDark && !previousState) {
        document.documentElement.classList.remove('dark');
      } else if (!forceDark && previousState) {
        document.documentElement.classList.add('dark');
      }
    };
  }, [forceDark]);
}

export default ThemeContext;
